/**
 * useMaritimeAnomaly — TanStack Query hook for the maritime anomaly
 * envelope consumed by the SignalLedger health banner.
 *
 * Cycle 101.1 sprint-1 Task 1.6 · beads `2sr0` / AC#6.
 *
 * Polls `/api/v1/maritime/anomaly?chokepoint=` on a 5-minute interval
 * matching the backend's in-memory cache TTL. Returns `null` when
 * disabled or before the first fetch resolves — the banner component
 * no-ops gracefully on null.
 */

import { useQuery } from '@tanstack/react-query';
import { maritimeApi, type MaritimeAnomalyEnvelope } from '../api/maritime';
import type { ChokepointId } from '../lib/chokepoints';

export interface UseMaritimeAnomalyOptions {
  readonly chokepoint: ChokepointId;
  /** Skip network fetch when true (e.g., when no maritime mode is
   *  active). Matches the consumer pattern elsewhere. */
  readonly enabled?: boolean;
}

const REFETCH_INTERVAL_MS = 5 * 60 * 1000; // align with backend 5-min cache

export function useMaritimeAnomaly({
  chokepoint,
  enabled = true,
}: UseMaritimeAnomalyOptions): MaritimeAnomalyEnvelope | null {
  const query = useQuery({
    queryKey: ['maritime', 'anomaly', chokepoint] as const,
    queryFn: () => maritimeApi.getAnomaly(chokepoint),
    enabled,
    refetchInterval: REFETCH_INTERVAL_MS,
    staleTime: REFETCH_INTERVAL_MS,
    retry: 0,
  });

  return query.data ?? null;
}
